import {
	useCurrentCompanyId,
	useCurrentCompanyValue,
	useCurrentProfileType,
	useCurrentUser,
	useHasNoRbacAccess,
} from "hooks/auth";
import { useCurrentUserHasDashboard } from "hooks/featureFlags";
import { ClientPaths } from "newui/utils/navigation/ClientPaths";
import { GuestPaths } from "newui/utils/navigation/GuestPaths";
import { SupplierPaths } from "newui/utils/navigation/SupplierPaths";
import { useCallback, useMemo } from "react";
import { CompanyProfileType } from "../types/CompanyProfileType";
import { BaseMemberPaths } from "newui/utils/navigation/navigation.types";

export interface NavigationPathsBase {
	clientPaths: ClientPaths | null;
	currentUserHomePage: string;
	guestPaths: GuestPaths | null;
	memberPaths: BaseMemberPaths | null;
	supplierPaths: SupplierPaths | null;
}
interface UseNavigationPaths extends NavigationPathsBase {
	getHomePageForProfileType: (
		profileType: CompanyProfileType,
		isAGuest?: boolean
	) => string;
}

export function useNavigationPaths(): UseNavigationPaths {
	const currentProfileType = useCurrentProfileType();

	const { isGuest } = useCurrentUser();
	const hasNoRbacAccess = useHasNoRbacAccess();
	const currentCompanyId = useCurrentCompanyId();
	const enableRfx = useCurrentCompanyValue("enable_rfx_functionality") ?? false;
	const hasDashboard = useCurrentUserHasDashboard();
	const navigationPaths = useMemo(() => {
		const allPaths = {
			clientPaths: null as ClientPaths,
			guestPaths: null as GuestPaths,
			supplierPaths: null as SupplierPaths,
			memberPaths: null as BaseMemberPaths,
		};

		if (currentCompanyId != null && !isNaN(currentCompanyId)) {
			allPaths.clientPaths = new ClientPaths(
				currentCompanyId,
				hasDashboard,
				enableRfx
			);
			allPaths.supplierPaths = new SupplierPaths(currentCompanyId);

			allPaths.memberPaths = allPaths.supplierPaths as BaseMemberPaths;
		}

		if (isGuest) {
			allPaths.guestPaths = new GuestPaths();
		}

		return allPaths;
	}, [currentCompanyId, isGuest, enableRfx, hasDashboard]);

	const getHomePageForProfileType = useCallback(
		(profileType: CompanyProfileType, isAGuest = false): string => {
			const login = "/login";

			if (isAGuest) {
				return navigationPaths.guestPaths.home ?? login;
			}

			if (
				profileType === CompanyProfileType.CLIENT &&
				navigationPaths.clientPaths
			) {
				return navigationPaths.clientPaths.home ?? login;
			} else if (
				profileType === CompanyProfileType.SUPPLIER &&
				navigationPaths.supplierPaths
			) {
				return navigationPaths.supplierPaths.home ?? login;
			}

			return login;
		},
		[navigationPaths]
	);

	const currentUserHomePage = useMemo(() => {
		/*
		 * PRO-737 outcome 3: a user holding no RBAC standing lands on My Profile,
		 * because it is the only page they can open — the normal home page would
		 * bounce them straight into the access-denied state on every login.
		 *
		 * Placed on this seam rather than in the login flow so that every caller
		 * of `currentUserHomePage` (login, profile switch, post-logout return)
		 * agrees on where such a user belongs.
		 */
		if (hasNoRbacAccess && navigationPaths.memberPaths?.myProfile) {
			return navigationPaths.memberPaths.myProfile;
		}

		return getHomePageForProfileType(currentProfileType, isGuest);
	}, [
		currentProfileType,
		isGuest,
		getHomePageForProfileType,
		hasNoRbacAccess,
		navigationPaths,
	]);

	return useMemo(
		() => ({
			...navigationPaths,
			currentUserHomePage,
			getHomePageForProfileType,
		}),
		[navigationPaths, currentUserHomePage, getHomePageForProfileType]
	);
}
